//////////////////////////////////////////////////
/// AREA: ソースインポート
//////////////////////////////////////////////////

// 独自モジュール
import { ClickEventHandler } from '../common/dom/ClickEvent';
import { ClassListController } from '../common/dom/ClassProcessor';
import { GraphicsLazyController } from '../common/media/Graphics';
import { FeatureModuleLoader } from '../common/loader/FeatureModule';

//////////////////////////////////////////////////
/// AREA: Feature対象セレクタ
//////////////////////////////////////////////////

// 汎用Featureは対象DOMが存在する場合だけ動的インポートする。その対象DOMを定義。
const FEATURE_SELECTORS = {
  MODAL_WINDOW: '[data-modal-root]',
  COLLAPSE: '[data-collapse-root]',
  TAB_SWITCH: '[data-tab-switch-root]',
  SCROLL_REVEAL: '[data-scroll-reveal-root], [data-scroll-reveal-item]',
  SCROLL_MOTION: '[data-scroll-motion-root], [data-scroll-motion-item]',
  IN_PAGE_NAV: '[data-in-page-nav-root]',
} as const;

//////////////////////////////////////////////////
/// AREA: グローバル変数
//////////////////////////////////////////////////
const gSiteBody: HTMLElement = document.body;
const gLazyTargets = [
  {
    selector: '.lazy_bg_image',
    options: {}
  },
  {
    selector: '.lazy_iframe',
    options: {
      rootMargin: '480px',
    }
  }
];

const gLazyController: GraphicsLazyController = new GraphicsLazyController(gLazyTargets);
const gFeatureModuleLoader: FeatureModuleLoader = new FeatureModuleLoader(gSiteBody);

let gScrollFlag: boolean = false;
let gResizeFlag: boolean = false;

//////////////////////////////////////////////////
/// AREA: Feature初期化
//////////////////////////////////////////////////
/**
 * // Note: 非同期処理
 *
 * async:
 * awaitを使用できる非同期関数として定義する。
 * async関数は必ずPromiseを返す。
 *
 * Promise<void>:
 * 非同期処理の完了を表すPromiseを返す型。
 * voidのため、完了時に利用する値そのものは返さない。
 *
 * await:
 * Promiseの完了を待ち、完了後に次の処理へ進む。
 * async関数内で使用する。
 *
 * Promise.all([...]):
 * 複数のPromiseをまとめて開始し、すべての完了を待つ。
 * 相互に依存しない非同期処理をまとめる場合に使用する。
 *
 * import(...):
 * モジュールを必要になった時点で動的に読み込む。
 * Promiseを返すため、読み込み結果を使用するときはawaitで完了を待つ。
 *
 * return Promise:
 * すでにPromiseを返す処理がある場合、そのPromiseを呼び出し元へそのまま返せる。
 * この場合、関数自身でawaitを使用しなければasyncは必須ではない。
 *
 * void functionName():
 * Promiseを返す非同期関数を実行するが、
 * 呼び出し元ではそのPromiseを待たず、戻り値も使用しないことを明示する。
 * 
 * 例）
 * ① initializeCollapse()
 * Promiseを「作る側へ処理を依頼して、そのPromiseを返す」
 *        ↓
 * (): Promise<void>
 *
 * ② async () => { await import(...) }
 * 自分自身でPromiseの完了を待つ
 *        ↓
 * async + await
 *
 * ③ initializeFeatures()
 * 複数のPromiseをまとめて待つ
 *        ↓
 * async + await Promise.all()
 */

/**
 * // Function: ページ内に存在する汎用Featureをまとめて初期化
 */
const initializeFeatures = async (): Promise<void> =>
{
  // 各Featureを開始し、すべての初期化完了を待つ
  await Promise.all([
    initializeCollapse(),
    initializeTabSwitch(),
    initializeScrollReveal(),
    initializeScrollMotion(),
    initializeInPageNav(),
  ]);
};

/**
 * // Function: Collapseを必要なページだけ読み込み・接続
 */
const initializeCollapse = (): Promise<void> =>
{
  return gFeatureModuleLoader.load(
    FEATURE_SELECTORS.COLLAPSE,
    // await import() を使用するため、このコールバックはasync
    async () =>
    {
      const { mountCollapseAll } = await import('../connectors/collapse');

      mountCollapseAll(gSiteBody, {
        closeOthersScope: 'none',
      });
    }
  );
};

/**
 * // Function: Tab Switchを必要なページだけ読み込み・接続
 */
const initializeTabSwitch = (): Promise<void> =>
{
  return gFeatureModuleLoader.load(FEATURE_SELECTORS.TAB_SWITCH, async () => 
  {
    const { mountTabSwitchAll } = await import('../connectors/tab-switch');

    mountTabSwitchAll(gSiteBody, {
      transition: {
        effects: ['fade'],
        flow: 'crossfade',
      }
    });
  });
};

/**
 * // Function: Scroll Revealを必要なページだけ読み込み・接続
 */
const initializeScrollReveal = (): Promise<void> =>
{
  return gFeatureModuleLoader.load(FEATURE_SELECTORS.SCROLL_REVEAL, async () =>
  {
    const { mountScrollRevealAll } = await import('../connectors/scroll-reveal');
    mountScrollRevealAll(gSiteBody);
  });
};

/**
 * // Function: Scroll Motionを必要なページだけ読み込み・接続
 */
const initializeScrollMotion = (): Promise<void> =>
{
  return gFeatureModuleLoader.load(FEATURE_SELECTORS.SCROLL_MOTION, async () =>
  {
    const { mountScrollMotionAll } = await import('../connectors/scroll-motion');
    mountScrollMotionAll(gSiteBody);
  });
};

/**
 * // Function: In-Page Navigatorをpresetごとの設定で読み込み・接続
 */
const initializeInPageNav = (): Promise<void> =>
{
  return gFeatureModuleLoader.load(FEATURE_SELECTORS.IN_PAGE_NAV, async () =>
  {
    const { mountInPageNavAll } = await import('../connectors/in-page-nav');
    const faq_selector = '[data-in-page-nav-root][data-in-page-nav-preset="faq"]';
    const service_selector = '[data-in-page-nav-root][data-in-page-nav-preset="service"]';

    mountInPageNavAll(gSiteBody, {
      rootSelector: faq_selector,
      index: {
        mode: 'toc',
      }
    });

    mountInPageNavAll(gSiteBody, {
      rootSelector: service_selector,
      index: {
        mode: 'button',
      }
    });
  });
};

//////////////////////////////////////////////////
/// AREA: Webサイト共通
//////////////////////////////////////////////////

/**
 * // Function: プロジェクト共通の動作をセットアップ
 */
const setupProjectBehaviors = (): void =>
{
  gLazyController.loadGraphics();
  gHeaderNav.clickToCloseDisplay();
  gHeaderNav.clickToToggleDisplay();
  gSideNav.scrollFixed();
  gScrollTopBtn.toPageTop();
  gScrollTopBtn.onAnimation();
  gCopyright.dispYear();
};

/// Part Name: グローバルナビ
const gHeaderNav = {
  ClickEventHandler: new ClickEventHandler(),
  mIcon: document.getElementById('gHeaderNavIcon'),
  mNav: document.getElementById('gHeaderNav'),
  mNavItems: null as HTMLCollectionOf<Element> | null,
  mToggleClassName: 'is_active',

  clickToToggleDisplay(): void
  {
    if (this.mIcon && this.mNav)
    {
      this.ClickEventHandler.mTrigger = this.mIcon;
      this.ClickEventHandler.mTarget = this.mNav;
      this.ClickEventHandler.toggleClassOnTarget(this.mToggleClassName);
    }
  },

  clickToCloseDisplay(): void
  {
    if (this.mNav)
    {
      this.mNavItems = this.mNav.getElementsByClassName('item');
      for (const item of this.mNavItems)
      {
        item.addEventListener('click', () =>
        {
          if (this.mIcon?.classList.contains(this.mToggleClassName) && this.mNav?.classList.contains(this.mToggleClassName))
          {
            this.mIcon.classList.remove(this.mToggleClassName);
            this.mNav.classList.remove(this.mToggleClassName);
          }
        });
      }
    }
  }
};

/// Part Name: サイドナビ
const gSideNav = {
  BREAK_POINT: 1201,
  mNav: document.getElementById('gSideNav'),

  scrollFixed(): void
  {
    if (this.mNav && window.innerWidth >= this.BREAK_POINT)
    {
      const nav_rect: DOMRect = this.mNav.getBoundingClientRect();
      const nav_height: number = nav_rect.height;
      const move_posY: string = 'calc(50% - ' + nav_height / 2 + 'px)';
      const nav_top: number = window.scrollY + nav_rect.top;

      if (window.scrollY > nav_top / 2)
      {
        this.mNav.style.position = 'fixed';
        this.mNav.style.insetBlockEnd = move_posY;
      }
      else
      {
        this.mNav.style.position = 'absolute';
        this.mNav.style.removeProperty('inset-block-end');
      }
    }
  }
};

/// Part Name: バックスクロールボタン
const gScrollTopBtn = {
  ClassListController: new ClassListController(),
  ClickEventHandler: new ClickEventHandler(),
  mButton: document.getElementById('gScrollTopBtn'),
  isDisplayed: false,
  onAnimation(target_height = 200): void
  {
    this.ClassListController.mTarget = this.mButton;
    const should_display: boolean = window.scrollY > target_height;

    if (should_display !== this.isDisplayed)
    {
      this.isDisplayed = should_display;
      if (should_display)
      {
        if (this.mButton) this.mButton.style.visibility = 'visible';
        this.ClassListController.switchClass('fadeOutDisp', 'fadeInDisp');
      }
      else
      {
        this.ClassListController.switchClass('fadeInDisp', 'fadeOutDisp');
      }
    }
  },

  toPageTop(): void
  {
    this.ClickEventHandler.mTrigger = this.mButton;
    this.ClickEventHandler.scrollToPageTop();
  }
};

/// Part Name: コピーライト
const gCopyright = {
  SINCE_YEAR: 2025,
  mDispYear: document.getElementById('dispYear'),
  Date: new Date(),

  getNowYear(): number
  {
    const now_year: number = this.Date.getFullYear();
    return now_year;
  },

  dispYear(): void
  {
    if (this.mDispYear)
    {
      if (this.SINCE_YEAR === this.getNowYear())
      {
        this.mDispYear.innerHTML += this.SINCE_YEAR;
      }
      else
      {
        this.mDispYear.innerHTML += `${this.SINCE_YEAR} - ${this.getNowYear()}`;
      }
    }
  }
};

//////////////////////////////////////////////////
/// AREA: イベント、メソッドの実行
//////////////////////////////////////////////////

document.addEventListener('DOMContentLoaded', () =>
{
  setupProjectBehaviors();
  // ページ内に対象DOMがあるFeatureだけ読み込み・接続する。
  // Feature初期化を開始するが、この場所では、完了を待たない
  void initializeFeatures();
});

window.addEventListener('scroll', () =>
{
  if (!gScrollFlag)
  {
    window.requestAnimationFrame(() =>
    {
      gScrollTopBtn.onAnimation();

      gScrollFlag = false;
    });
    gScrollFlag = true;
  }
});

window.addEventListener('resize', () =>
{
  if (!gResizeFlag)
  {
    window.requestAnimationFrame(() =>
    {
      gScrollTopBtn.onAnimation();

      gResizeFlag = false;
    });
    gResizeFlag = true;
  }
});

//////////////////////////////////////////////////
/// AREA: END
//////////////////////////////////////////////////


